import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Search, type LucideIcon } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { CLUSTER_ORDER, CLUSTER_META, clusterRoute, presentationOf } from "@/entities/capability";
import { SCENE_CARDS } from "@/entities/scene-card/catalog";
import { useClusterCards } from "@/pages/workbench/useClusterCards";

interface Command {
  id: string;
  kind: "cluster" | "module";
  label: string;
  sub: string;
  route: string;
  icon: LucideIcon;
  /** 舱别名词面(小写),来自场景卡目录派生 —— 见 {@link keywordsForRoute}。 */
  keywords?: string;
}

/**
 * 舱别名词表:从场景卡目录**派生**(0816 琳琳/爱民/玉玉「搜『海报』不出视觉舱」)。
 *
 * 舱名 2026-07-24 改成「视觉舱/视频舱/…」后,用户嘴里的「海报/长图/套图/换衣」在
 * label/sub 里一个都不剩,⌘K 输人话恒空。别名不手写枚举(pro_plus 三次发作的课):
 * 卡目录的「标题+一句话用途+标签」本来就是给用户读的人话,按路由前缀归舱拼进
 * keywords —— 上新一张卡,词表自动跟上。
 */
function keywordsForRoute(route: string): string {
  // route 可为 null(即将卡/导流卡)—— 第一版没防,6 条既有测试全红当场抓回
  return SCENE_CARDS.filter((c) => c.route?.startsWith(route))
    .map((c) => `${c.title} ${c.usage} ${(c.tags ?? []).join(" ")}`)
    .join(" ")
    .toLowerCase();
}

/**
 * ⌘K 命令面板(2026-07-25 发现流尾巴)—— 快速跳 **群 / 舱**。
 *
 * ★纯轨壳没有搜索框,深处的舱要靠「首页 → 群页 → 舱」两跳;⌘K 给键盘党一条直达:
 *   搜群名去群页、搜舱名直接进舱页。命令源 = CLUSTER_META(5 群)+ useClusterCards 各群已开舱(单源,
 *   与首页/群页同口径:占位/未开通舱无 route 不入命令,不给点了 gate 的假直达)。
 * ★不用 Radix Dialog:受控无 Trigger 会丢焦点到 body(记忆坑)。自建 overlay,全控输入焦点 + 方向键。
 * ★仅租户壳;平台超管走 AppSidebar,不挂本面板。
 */
export function CommandPalette() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { effectiveScope } = useAuth();
  // OEM W3:渠道管理员(PARTNER)与平台超管同属后台壳,不拉租户卡墙、不挂本面板。
  const isPlatform = effectiveScope === "PLATFORM" || effectiveScope === "PARTNER";
  const { forCluster } = useClusterCards({ enabled: !isPlatform });

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // 全局 ⌘K / Ctrl+K 开关(输入框内也能开;Esc 关)。
  // A1(2026-08-17):顶栏搜索按钮键盘党之外的鼠标入口 —— 派 `app:open-command-palette`
  //   自定义事件打开(避免把 open 态提到 context,改动最小、不动本组件其余逻辑)。
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("app:open-command-palette", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("app:open-command-palette", onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQ("");
      setIdx(0);
      // 等 overlay 挂载后聚焦输入
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const commands = useMemo<Command[]>(() => {
    if (isPlatform) return [];
    const list: Command[] = [];
    for (const cluster of CLUSTER_ORDER) {
      const meta = CLUSTER_META[cluster];
      const clusterLabel = t(meta.labelKey);
      list.push({
        id: `cluster-${cluster}`,
        kind: "cluster",
        label: clusterLabel,
        sub: t("shell.commandClusterSub"),
        route: clusterRoute(cluster),
        icon: meta.railIcon,
      });
      const { depts, caps } = forCluster(cluster);
      for (const d of depts) {
        list.push({
          id: `mod-${d.moduleKey}`,
          kind: "module",
          label: t(d.titleKey, { defaultValue: d.title }),
          sub: clusterLabel,
          route: d.route,
          icon: d.icon,
          keywords: keywordsForRoute(d.route),
        });
      }
      for (const c of caps) {
        const pres = presentationOf(c.capability);
        if (!pres || c.status !== "active" || !pres.route) continue; // 占位/未开通不入命令
        list.push({
          id: `mod-${c.capability}`,
          kind: "module",
          label: t(`cabin.name.${c.capability}`, { defaultValue: c.displayName }),
          sub: clusterLabel,
          route: pres.route,
          icon: pres.icon,
          keywords: keywordsForRoute(pres.route),
        });
      }
    }
    return list;
    // forCluster 每次渲染新引用;命令构建廉价,按 q/open 触发即可
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatform, t, open]);

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    if (!kw) return commands;
    return commands.filter(
      (c) =>
        c.label.toLowerCase().includes(kw) ||
        c.sub.toLowerCase().includes(kw) ||
        // 舱别名(场景卡目录派生):搜「海报/长图/套图」这类人话也要到舱
        (c.keywords?.includes(kw) ?? false)
    );
  }, [commands, q]);

  if (isPlatform || !open) return null;

  const go = (cmd: Command | undefined) => {
    if (!cmd) return;
    setOpen(false);
    navigate(cmd.route);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(filtered[idx]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    // backdrop 点击关闭 = 鼠标便利;键盘等价是输入框上的 Esc(见 onKeyDown),故 a11y 键盘监听豁免。
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-foreground/40 p-4 pt-[12vh] backdrop-blur-sm"
      onClick={() => setOpen(false)}
      data-command-palette
    >
      {/* 面板阻止冒泡(点面板内不关);非交互元素,仅拦点击 */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="w-full max-w-lg overflow-hidden rounded-[calc(var(--radius)*1.2)] border bg-popover shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b px-4">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setIdx(0);
            }}
            onKeyDown={onKeyDown}
            placeholder={t("shell.commandPlaceholder")}
            aria-label={t("shell.commandPlaceholder")}
            className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        {/* data-skin-plain:这个 <ul> 是命令面板内嵌的结果列表,不是独立浮层。没有它,
            `[data-skin="comic"] [role="listbox"]` 会给它刷上 3px 描边 + 20px 圆角,
            在面板自己的框里再叠一张卡(2026-08-07 走查真引擎实测)。 */}
        <ul className="max-h-[52vh] overflow-y-auto p-2" role="listbox" data-skin-plain>
          {filtered.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t("shell.commandEmpty")}
            </li>
          )}
          {filtered.map((cmd, i) => {
            const Icon = cmd.icon;
            return (
              <li key={cmd.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === idx}
                  onMouseEnter={() => setIdx(i)}
                  onClick={() => go(cmd)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
                    i === idx
                      ? "bg-accent text-accent-foreground"
                      : "text-foreground hover:bg-muted"
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{cmd.label}</span>
                  <span className="shrink-0 text-2xs text-muted-foreground">{cmd.sub}</span>
                  {i === idx && (
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
